import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare, faCircleUp, faCircleDown, } from '@fortawesome/free-regular-svg-icons';
import { faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '../../components/UI/Modal';
import { Code } from '../../components/UI/Code';
import { Loading } from '../../components/UI/Loading';
import { QuizForm } from '../../components/Quiz/QuizForm';
import { QuestionItem } from '../../components/Question/QuestionItem';
import { QuestionForm } from '../../components/Question/QuestionForm';
import { QuestionItemSkeleton } from '../../components/UI/QuestionItemSkeleton';
import { getQuizzes, getQuiz, updateQuiz, updateQuizStatus, updateQuestion, addQuestion, removeQuestion, deleteQuiz, } from '../../redux/actions/quizzes';
import { setEditingQuestionId, clearError } from '../../redux/reducers/quizzes';
import { configService } from '../../services/config.service';
const { playServer } = await configService.appConfig;
const EditQuizComponent = ({ quizDetail, getQuiz, setEditingQuestionId, clearError, updateQuiz, updateQuizStatus, deleteQuiz, addQuestion, updateQuestion, removeQuestion, loading, error, errorDetail, editingQuestionId, }) => {
    const [isAddQuestionOpen, setIsAddQuestionOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const params = useParams();
    const quizId = params.id;
    /**
     * Effect to fetch quiz details if not already loaded
     * Cleans up editing state on unmount
     */
    useEffect(() => {
        // Clear any previous errors and fetch quiz details
        if (quizId) {
            clearError({});
            getQuiz(quizId);
        }
        return () => {
            setEditingQuestionId({ questionId: undefined });
        };
    }, [quizId, getQuiz, clearError]);
    // Guard placed after all hooks so hook order stays stable across renders
    if (!quizId)
        return React.createElement("div", null, "Quiz not found");
    /**
     * Additional check to ensure we have the correct quiz loaded
     * If the loaded quiz doesn't match the current quizId, show loading
     */
    const isCorrectQuizLoaded = quizDetail && quizDetail.quizId === quizId;
    const emptyQuestion = {
        text: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        answer: 'A',
    };
    const handleCodeCopy = () => {
        setCopied(true);
    };
    const handleModalOpen = (event) => {
        event.preventDefault();
        setIsModalOpen(true);
        setCopied(false);
    };
    const handleModalClose = () => {
        setIsModalOpen(false);
    };
    const handleStatusUpdate = (event) => {
        event.preventDefault();
        if (quizDetail) {
            updateQuizStatus({
                quizId,
                status: quizDetail.status === 'draft' ? 'published' : 'draft',
            });
        }
    };
    /**
     * Handles quiz metadata updates
     */
    const handleQuizUpdate = (quizData) => {
        if (quizDetail) {
            updateQuiz({
                quizId: quizDetail.quizId,
                data: {
                    name: quizData.name,
                    duration: quizData.duration,
                    settings: quizData.settings,
                },
            });
        }
        setIsEditing(false);
    };
    const handleEditCancel = () => {
        setIsEditing(false);
    };
    /**
     * Handles quiz deletion and navigates back to quiz list
     */
    const handleQuizDelete = () => {
        if (quizDetail) {
            deleteQuiz(quizDetail.quizId);
            navigate('/quizzes');
        }
    };
    /**
     * Sets a question into edit mode
     */
    const handleEditingQuestionItem = (question) => {
        setEditingQuestionId({ questionId: question.questionId });
    };
    const handleCancelEditingQuestionItem = () => {
        setEditingQuestionId({ questionId: undefined });
    };
    /**
     * Handles updates to existing questions
     */
    const handleQuestionItemUpdate = (question) => {
        if (quizDetail) {
            updateQuestion({
                quizId: quizDetail.quizId,
                questionId: editingQuestionId,
                question,
            });
        }
    };
    /**
     * Handles adding new questions to the quiz
     */
    const handleAddQuestionItem = (question) => {
        if (quizDetail) {
            addQuestion({ quizId: quizDetail.quizId, question });
            setIsAddQuestionOpen(false);
        }
    };
    /**
     * Handles question deletion
     */
    const handleRemoveWordItem = (question) => {
        if (quizDetail) {
            removeQuestion({
                quizId: quizDetail.quizId,
                questionId: question.questionId,
            });
        }
    };
    // Show loading while fetching data
    if (loading)
        return React.createElement(Loading, null);
    // Show error message if there's an API error
    if (error) {
        return (React.createElement("div", { className: "error-container" },
            React.createElement("h3", null, "Error loading quiz"),
            React.createElement("p", null, errorDetail || 'Failed to load quiz details. Please try again.'),
            React.createElement("button", { className: "btn--primary", onClick: () => {
                    clearError({});
                    getQuiz(quizId);
                } }, "Retry")));
    }
    // Show error if quiz not found after successful API call
    if (!loading && !quizDetail) {
        return (React.createElement("div", { className: "error-container" },
            React.createElement("h3", null, "Quiz not found"),
            React.createElement("p", null, "The quiz you're looking for doesn't exist or has been deleted."),
            React.createElement("button", { className: "btn--primary", onClick: () => window.history.back() }, "Go Back")));
    }
    // Show loading if we don't have the correct quiz loaded yet
    if (!isCorrectQuizLoaded)
        return React.createElement(Loading, null);
    return (React.createElement(React.Fragment, null,
        quizDetail && quizDetail.code && (React.createElement(Modal, { id: quizDetail.code, component: Code, componentProps: {
                id: quizDetail.code,
                link: `${playServer}?code=${quizDetail.code}`,
                copied,
                onCopy: handleCodeCopy,
            }, isOpen: isModalOpen, onClose: handleModalClose })),
        ' ',
        quizDetail && (React.createElement("div", { className: "activity-container" },
            isEditing ? (React.createElement(QuizForm, { quiz: quizDetail, onSubmit: handleQuizUpdate, onCancel: handleEditCancel })) : (React.createElement("div", { className: "activity-item-no-hover" },
                React.createElement("div", { className: "activity-header" },
                    React.createElement("h2", null, quizDetail.name)),
                React.createElement("div", { className: "activity-settings" },
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: quizDetail.settings.timed === true
                                ? faSquareCheck
                                : faSquare }),
                        "Timed"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: quizDetail.settings.shuffleQuestions === true
                                ? faSquareCheck
                                : faSquare }),
                        "Shuffle Questions"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: quizDetail.settings.sendResultEmail === true
                                ? faSquareCheck
                                : faSquare }),
                        "Send Result Email"),
                    React.createElement("span", null,
                        React.createElement(FontAwesomeIcon, { icon: quizDetail.settings.public === true
                                ? faSquareCheck
                                : faSquare }),
                        "Share Publicly")),
                quizDetail.duration && (React.createElement("div", { className: "activity-meta" },
                    React.createElement("span", null,
                        "Duration: ",
                        parseInt(quizDetail.duration) / 60,
                        " min",
                        ' '))),
                React.createElement("div", { className: "activity-actions" },
                    React.createElement("button", { className: "btn--primary", onClick: () => {
                            setIsEditing(true);
                        }, disabled: quizDetail.status === 'published' }, "Edit"),
                    React.createElement("button", { className: "btn--primary publish", onClick: handleStatusUpdate, disabled: quizDetail.questions.length === 0 },
                        React.createElement(FontAwesomeIcon, { icon: quizDetail.status === 'draft' ? faCircleUp : faCircleDown }),
                        quizDetail.status === 'draft' ? 'Publish' : 'Unpublish'),
                    React.createElement("button", { className: "btn--secondary share", value: quizDetail.code, onClick: handleModalOpen, disabled: quizDetail.status === 'draft' },
                        React.createElement(FontAwesomeIcon, { icon: faShareNodes }),
                        "Host"),
                    React.createElement("button", { className: "btn--delete", onClick: handleQuizDelete, disabled: quizDetail.status === 'published' }, "Delete")))),
            quizDetail.status === 'draft' && (React.createElement("button", { className: isAddQuestionOpen ? 'btn--secondary' : 'btn--primary', onClick: (_e) => {
                    const form = document.getElementById('create');
                    if (form) {
                        if (isAddQuestionOpen) {
                            form.style.display = 'none';
                        }
                        else {
                            form.style.display = 'flex';
                        }
                    }
                    setIsAddQuestionOpen(!isAddQuestionOpen);
                } }, isAddQuestionOpen ? 'Cancel' : 'Add Question')),
            React.createElement(QuestionForm, { action: "create", question: emptyQuestion, onAdd: handleAddQuestionItem, onCancel: () => { } }),
            quizDetail.questions && quizDetail.questions.length > 0 && (React.createElement("div", { className: "activity-content" }, quizDetail.questions.map((question) => editingQuestionId === question.questionId ? (question.loading ? (React.createElement(QuestionItemSkeleton, { key: question.questionId })) : (React.createElement(QuestionForm, { action: "edit", key: question.questionId, question: question, onAdd: handleQuestionItemUpdate, onCancel: handleCancelEditingQuestionItem }))) : (React.createElement(QuestionItem, { key: question.questionId, question: question, quizStatus: quizDetail.status, onEdit: handleEditingQuestionItem, onDelete: handleRemoveWordItem })))))))));
};
/**
 * Maps Redux state to component props
 */
const mapStateToProps = (state) => {
    return {
        quizzes: state.quizState.quizzes,
        quizDetail: state.quizState.quizDetail,
        loading: state.quizState.loading,
        error: state.quizState.error,
        errorDetail: state.quizState.errorDetail,
        editingQuestionId: state.quizState.editingQuestionId,
    };
};
/**
 * Redux action creators to be bound to component props
 */
const mapDispatchToProps = {
    getQuizzes,
    getQuiz,
    updateQuiz,
    updateQuizStatus,
    addQuestion,
    updateQuestion,
    removeQuestion,
    setEditingQuestionId,
    clearError,
    deleteQuiz,
};
export const EditQuiz = connect(mapStateToProps, mapDispatchToProps)(EditQuizComponent);
